import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Collapse,
  DialogContentText,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import PropTypes from "prop-types";

import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";
import FireSafetyViewBuildingObservationDetails from "./FireSafetyViewBuildingObservationDetails";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import {
  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
  FIRE_SAFETY_CONSUMER,
  fireSafetyScheduleStatusTypes,
} from "common/constant";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { getImageUrl } from "utils";

import {
  useGetBuildingSchedulesQuery,
  useLazyFireSafetyBuildingObservationQuery,
  useLazyGetNoticeUrlByScheduleId,
  usePutSendNoticeMutation,
} from "apis/FireSafety/FireSafetyObservation";

const FireSafetyViewBuildingSchedules = ({ handleClose, applicationId, t }) => {
  const { roles } = useSelector((state) => state.Login);

  const {
    data: { data: buildingSchedulesData } = {},
    isLoading,
    isFetching,
  } = useGetBuildingSchedulesQuery(applicationId);

  const [sendNotice, { isLoading: isNoticeSending }] =
    usePutSendNoticeMutation();

  const [
    getBuildingObservationDataById,
    {
      isLoading: isBuildingObservationDataLoading,
      isFetching: isBuildingObservationDataFetching,
      data: { data: buildingObservationData } = {},
    },
  ] = useLazyFireSafetyBuildingObservationQuery();

  const [getNoticeUrlData, { isLoading: isPreviewNoticeLoading }] =
    useLazyGetNoticeUrlByScheduleId();

  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [scheduleIdForSendNotice, setScheduleIdForSendNotice] = useState("");
  const [scheduleId, setScheduleId] = useState("");

  const sendNoticeBtnHandler = async () => {
    try {
      await sendNotice(scheduleIdForSendNotice);
      setScheduleIdForSendNotice("");
      handleClose();
    } catch (error) {}
  };

  const handleToggle = (id) => {
    if (scheduleId === id) {
      setScheduleId("");
    } else {
      setScheduleId(id);
    }
  };

  const previewNotice = async (scheduleId) => {
    try {
      const { data: { response: url } = {} } = await getNoticeUrlData(
        scheduleId
      );
      setDocumentToPreview({
        url: getImageUrl(url),
        documentTypeName: "Buiding Observation Notice",
        documentType: "pdf",
        documentName: "buildingObservationNotice",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const buildingObservation = useMemo(() => {
    const { observations } = buildingObservationData || {};
    if (roles.includes(FIRE_SAFETY_CONSUMER) && observations?.length) {
      return observations.filter(({ isApproved }) => isApproved);
    }
    return observations;
  }, [buildingObservationData, roles]);

  useEffect(() => {
    if (scheduleId) {
      (async () => {
        try {
          await getBuildingObservationDataById(scheduleId);
        } catch (error) {
          console.error(error);
        }
      })();
    }
  }, [scheduleId]);

  useEffect(() => {
    if (buildingSchedulesData?.length === 1) {
      setScheduleId(buildingSchedulesData[0].scheduleId);
    }
  }, [buildingSchedulesData]);

  if (isLoading || isFetching) {
    return <Backdrop open />;
  }

  return (
    <>
      <Backdrop open={isPreviewNoticeLoading} />
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        title={t("Building Observations")}
        maxWidth="lg"
      >
        {!!scheduleIdForSendNotice && (
          <ConfirmationDialog
            message="Are you sure you want to send notice for this schedule?"
            onCancel={() => setScheduleIdForSendNotice("")}
            onConfirm={sendNoticeBtnHandler}
            isOpen={scheduleIdForSendNotice}
            title="Warning"
            variant="warning"
            isLoading={isNoticeSending}
          />
        )}
        {buildingSchedulesData?.length ? (
          buildingSchedulesData.map(
            ({
              startDate,
              endDate,
              scheduleId: id,
              status,
              isObservationNoticeResend,
            }) => (
              <Fragment key={id}>
                <div
                  className="fs-4 fw-semibold mb-3 border-bottom"
                  onClick={() => handleToggle(id)}
                  key={id}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className="cursor-pointer"
                  >
                    <div className="d-flex align-items-center gap-1">
                      <p className="fw-bold mb-0">{`${convertUTCDateToLocalDate(
                        startDate
                      ).format("DD-MM-YYYY")} - ${convertUTCDateToLocalDate(
                        endDate
                      ).format("DD-MM-YYYY")}`}</p>
                    </div>
                    <IconButton>
                      {scheduleId === id ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                </div>
                <DialogContentText variant="subtitle2" component="div">
                  <Collapse in={scheduleId === id} timeout="auto" unmountOnExit>
                    {isBuildingObservationDataFetching ||
                    isBuildingObservationDataLoading ? (
                      <div className="d-flex justify-content-center">
                        <CircularProgress size={30} />
                      </div>
                    ) : (
                      <>
                        {!!buildingObservation?.length &&
                        buildingObservation.map((observationDetails, index) => (
                              <FireSafetyViewBuildingObservationDetails
                                key={index}
                                observationDetails={observationDetails}
                                index={index}
                                buildingInformationId={
                                  buildingObservationData.buildingInformationId
                                }
                              />
                        ))}
                      </>
                    )}
                    {(status === fireSafetyScheduleStatusTypes["In Progress"] ||
                      isObservationNoticeResend) &&
                      roles.includes(FIRE_EMERGENCY_CHIEF_FIRE_OFFICER) && (
                        <div className="text-end">
                          <Grid
                            container
                            rowSpacing={1}
                            columnSpacing={2}
                            sx={{ justifyContent: "end" }}
                          >
                            <Grid item>
                              <Button
                                color="primary"
                                variant="contained"
                                onClick={() => previewNotice(id)}
                              >
                                {t("Preview Notice")}
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                color="success"
                                variant="contained"
                                onClick={() => setScheduleIdForSendNotice(id)}
                              >
                                {t("Send Notice")}
                              </Button>
                            </Grid>
                          </Grid>
                        </div>
                      )}
                  </Collapse>
                </DialogContentText>
              </Fragment>
            )
          )
        ) : (
          <Typography variant="h6" className="text-center mt-3">
            {t("There is no observations added for this building till now")}
          </Typography>
        )}
      </Modal>
    </>
  );
};

FireSafetyViewBuildingSchedules.propTypes = {
  handleClose: PropTypes.func,
  applicationId: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyViewBuildingSchedules);
